import { inject, Injectable } from '@angular/core';
import { Bookmark } from '../models/bookmark';
import {
  BookmarkValidationIssue,
  BookmarkValidationResult,
  BookmarkValidationSummary,
} from '../models/bookmark-validation';
import { CustomGridStackWidget } from './grid-widget-config.service';
import { IndicatorDataStore } from '../stores/indicatpr-data/indicator-data.state';
import { DataLayerStore } from '../stores/data-layer/data-layer.state';
import { IndicatorStore } from '../stores/indicator/indicator.state';
import { IndicatorData } from '../models/indicator-data';
import { DataLayer } from '../models/data-layer';
import { Indicator } from '../models/indicator';
import { ActiveUserStore } from '../stores/user/active-user.state';
import { WorkspaceStore } from '../stores/workspace/workspace.state';
import { BookmarkStore } from '../stores/bookmark/bookmark.state';
import { DataLayerTypeStore } from '../stores/data-layer-type/data-layer-type.state';

@Injectable({
  providedIn: 'root',
})
export class BookmarkValidationService {
  private readonly indicatorDataStore = inject(IndicatorDataStore);
  private readonly dataLayerStore = inject(DataLayerStore);
  private readonly dataLayerTypeStore = inject(DataLayerTypeStore);
  private readonly indicatorStore = inject(IndicatorStore);
  private readonly bookmarkStore = inject(BookmarkStore);
  private readonly activeUserStore = inject(ActiveUserStore);

  /**
   * Validates a single bookmark for publication issues
   * @param bookmark The bookmark to validate
   * @returns Validation result with all found issues
   */
  validateBookmark(bookmark: Bookmark): BookmarkValidationResult {
    const issues: BookmarkValidationIssue[] = [];

    // Only validate public bookmarks (private bookmarks don't need validation)
    if (bookmark.private || !bookmark.published) {
      return {
        bookmarkId: bookmark._id,
        bookmarkTitle: bookmark.title,
        isValid: true,
        issues: [],
        validatedAt: new Date(),
      };
    }

    // Check each widget in the bookmark configuration
    if (bookmark.config && Array.isArray(bookmark.config)) {
      bookmark.config.forEach((widget: CustomGridStackWidget) => {
        const widgetIssues = this.validateWidget(widget, bookmark);
        issues.push(...widgetIssues);
      });
    }

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      isValid: issues.length === 0,
      issues,
      validatedAt: new Date(),
    };
  }

  /**
   * Validates a single widget for publication issues
   * @param widget The widget to validate
   * @param bookmark The parent bookmark
   * @returns Array of validation issues found
   */
  private validateWidget(
    widget: CustomGridStackWidget,
    bookmark: Bookmark
  ): BookmarkValidationIssue[] {
    const issues: BookmarkValidationIssue[] = [];

    // Skip content widgets (text, teaser, info)
    if (!widget.widget || ['text', 'teaser', 'info'].includes(widget.type)) {
      return issues;
    }

    const widgetConfig = widget.widget;

    // Get all data layers from widget
    const dataLayerIds = this.getDataLayerIdsFromWidget(widgetConfig);

    // Get all indicators from widget
    const indicatorIds = this.getIndicatorIdsFromWidget(widgetConfig);

    // Check each combination of dataLayer and indicator
    dataLayerIds.forEach(dataLayerId => {
      const dataLayer = this.dataLayerStore.getById(dataLayerId);

      if (!dataLayer) {
        // DataLayer not found - create missing-datalayer issue
        issues.push(
          this.createIssue(
            bookmark,
            widget,
            'missing-datalayer',
            undefined,
            undefined,
            undefined,
            dataLayerId
          )
        );
        return; // Skip further checks for this dataLayer
      }

      // Check if dataLayer is published
      if (!dataLayer.published) {
        issues.push(
          this.createIssue(
            bookmark,
            widget,
            'unpublished-datalayer',
            dataLayer,
            undefined,
            undefined
          )
        );
      }

      indicatorIds.forEach(indicatorId => {
        const indicator = this.indicatorStore.getById(indicatorId);

        if (!indicator) {
          // Indicator not found - create missing-indicator issue
          issues.push(
            this.createIssue(
              bookmark,
              widget,
              'missing-indicator',
              dataLayer,
              undefined,
              undefined,
              indicatorId
            )
          );
          return; // Skip further checks for this indicator
        }

        // Find the IndicatorData for this combination
        const indicatorData =
          this.indicatorDataStore.getByDataLayerAndIndicator(
            dataLayerId,
            indicatorId
          );

        if (indicatorData) {
          // Check if IndicatorData is published
          if (!indicatorData.published) {
            issues.push(
              this.createIssue(
                bookmark,
                widget,
                'unpublished-data',
                dataLayer,
                indicator,
                indicatorData
              )
            );
          }
        }
      });
    });

    return issues;
  }

  /**
   * Extracts all dataLayer IDs from a widget configuration
   * @param widgetConfig The widget configuration
   * @returns Array of dataLayer IDs
   */
  private getDataLayerIdsFromWidget(widgetConfig: any): string[] {
    const ids: string[] = [];

    // Single dataLayer
    if (widgetConfig.dataLayer) {
      ids.push(widgetConfig.dataLayer);
    }

    // Multiple dataLayers
    if (
      widgetConfig.dataLayers &&
      Array.isArray(widgetConfig.dataLayers) &&
      widgetConfig.dataLayers.length > 0
    ) {
      ids.push(...widgetConfig.dataLayers);
    }

    return [...new Set(ids)]; // Remove duplicates
  }

  /**
   * Extracts all indicator IDs from a widget configuration
   * @param widgetConfig The widget configuration
   * @returns Array of indicator IDs
   */
  private getIndicatorIdsFromWidget(widgetConfig: any): string[] {
    const ids: string[] = [];

    // Single indicator
    if (widgetConfig.indicator) {
      ids.push(widgetConfig.indicator);
    }

    // Multiple indicators
    if (
      widgetConfig.indicators &&
      Array.isArray(widgetConfig.indicators) &&
      widgetConfig.indicators.length > 0
    ) {
      ids.push(...widgetConfig.indicators);
    }

    // Axis properties for scatter/bubble charts
    if (
      widgetConfig.axisProperties &&
      Array.isArray(widgetConfig.axisProperties)
    ) {
      ids.push(...widgetConfig.axisProperties);
    }

    return [...new Set(ids)]; // Remove duplicates
  }

  /**
   * Creates a validation issue object
   */
  private createIssue(
    bookmark: Bookmark,
    widget: CustomGridStackWidget,
    issueType: BookmarkValidationIssue['issueType'],
    dataLayer?: DataLayer,
    indicator?: Indicator,
    indicatorData?: IndicatorData,
    missingId?: string
  ): BookmarkValidationIssue {
    let message = '';

    const dataLayerType = dataLayer
      ? this.dataLayerTypeStore.getById(dataLayer.parent)
      : null;

    switch (issueType) {
      case 'unpublished-datalayer':
        message = `Jahr "${dataLayer?.year} (${dataLayerType?.name || 'Raum unbekannt'})" ist nicht veröffentlicht`;
        break;
      case 'unpublished-indicator':
        message = `Indikator "${indicator?.name}" ist nicht veröffentlicht`;
        break;
      case 'unpublished-data':
        message = `Daten für "${indicator?.name}" im Jahr "${dataLayer?.year} (${dataLayerType?.name || 'Raum unbekannt'})" sind nicht veröffentlicht`;
        break;
      case 'missing-datalayer':
        message = `Jahr mit ID "${missingId}" wurde nicht gefunden`;
        break;
      case 'missing-indicator':
        message = `Indikator mit ID "${missingId}" wurde nicht gefunden`;
        break;
    }

    // Generate widget title
    const widgetTitle = this.getWidgetTitle(widget, indicator);

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      issueType,
      widgetId: String(widget.id),
      widgetType: widget.type,
      widgetHeadline: widgetTitle,
      details: {
        dataLayerId: dataLayer?._id || missingId,
        dataLayerName: dataLayer?.year ? String(dataLayer.year) : missingId,
        indicatorId: indicator?._id || missingId,
        indicatorName: indicator?.name || missingId,
        indicatorDataId: indicatorData?._id,
      },
      message,
    };
  }

  /**
   * Gets a descriptive title for a widget
   * Priority: 1. Custom headline, 2. Indicator name, 3. Widget type
   */
  private getWidgetTitle(
    widget: CustomGridStackWidget,
    indicator?: Indicator
  ): string {
    // First priority: custom headline
    if (widget.widget?.headline) {
      return widget.widget.headline;
    }

    // Second priority: indicator name
    if (indicator?.name) {
      return indicator.name;
    }

    // Third priority: widget type with multiple indicators
    if (widget.widget?.indicators && widget.widget.indicators.length > 0) {
      const firstIndicator = this.indicatorStore.getById(
        widget.widget.indicators[0]
      );
      if (firstIndicator) {
        return `${firstIndicator.name} (+${widget.widget.indicators.length - 1})`;
      }
    }

    // Fallback: widget type label
    return this.getWidgetTypeLabel(widget.type);
  }

  /**
   * Gets a human-readable label for widget type
   */
  private getWidgetTypeLabel(type: string): string {
    const labels: { [key: string]: string } = {
      map: 'Karte',
      bar: 'Balken-/Säulendiagramm',
      line: 'Liniendiagramm',
      pie: 'Kreis-/Ringdiagramm',
      scatter: 'Streudiagramm',
      bubble: 'Blasendiagramm',
      radar: 'Netz-/Radardiagramm',
      polarArea: 'Polardiagramm',
      table: 'Tabelle',
      text: 'Text',
      info: 'Indikatorinfo',
      teaser: 'Teaser/Bild',
    };

    return labels[type] || type;
  }

  /**
   * Validates multiple bookmarks
   * @param bookmarks Array of bookmarks to validate
   * @returns Summary of validation results
   */
  validateBookmarks(bookmarks: Bookmark[]): BookmarkValidationSummary {
    const results = bookmarks.map(bookmark => this.validateBookmark(bookmark));

    const validBookmarks = results.filter(r => r.isValid).length;
    const invalidBookmarks = results.filter(r => !r.isValid).length;
    const totalIssues = results.reduce((sum, r) => sum + r.issues.length, 0);

    return {
      totalBookmarks: bookmarks.length,
      validBookmarks,
      invalidBookmarks,
      totalIssues,
      results,
      validatedAt: new Date(),
    };
  }

  /**
   * Validates only public bookmarks
   * @param bookmarks Array of all bookmarks
   * @returns Summary of validation results for public bookmarks only
   */
  validatePublicBookmarks(bookmarks: Bookmark[]): BookmarkValidationSummary {
    const publicBookmarks = bookmarks.filter(b => !b.private && b.published);
    return this.validateBookmarks(publicBookmarks);
  }

  /**
   * Validates access permissions for all bookmarks based on user's workspace
   * @param bookmarks Array of all bookmarks
   * @returns Summary with both public validation AND access validation AND data validation
   */
  validateBookmarksWithAccess(
    bookmarks?: Bookmark[]
  ): BookmarkValidationSummary {
    const bookmarksToValidate = bookmarks || this.bookmarkStore.asArray();
    // Get public validation (existing functionality)
    const publicValidation = this.validatePublicBookmarks(bookmarksToValidate);

    // Get access validation (new functionality)
    const accessResults = bookmarksToValidate.map(bookmark =>
      this.validateBookmarkAccess(bookmark)
    );

    const validAccessBookmarks = accessResults.filter(r => r.isValid).length;
    const invalidAccessBookmarks = accessResults.filter(r => !r.isValid).length;
    const totalAccessIssues = accessResults.reduce(
      (sum, r) => sum + r.issues.length,
      0
    );

    // Get data availability validation (checks ALL bookmarks)
    const dataResults = bookmarksToValidate.map(bookmark =>
      this.validateBookmarkDataAvailability(bookmark)
    );

    const validDataBookmarks = dataResults.filter(r => r.isValid).length;
    const invalidDataBookmarks = dataResults.filter(r => !r.isValid).length;
    const totalDataIssues = dataResults.reduce(
      (sum, r) => sum + r.issues.length,
      0
    );

    return {
      ...publicValidation,
      accessValidation: {
        totalBookmarks: bookmarksToValidate.length,
        validBookmarks: validAccessBookmarks,
        invalidBookmarks: invalidAccessBookmarks,
        totalIssues: totalAccessIssues,
        results: accessResults,
      },
      dataValidation: {
        totalBookmarks: bookmarksToValidate.length,
        validBookmarks: validDataBookmarks,
        invalidBookmarks: invalidDataBookmarks,
        totalIssues: totalDataIssues,
        results: dataResults,
      },
    };
  }

  /**
   * Validates a single bookmark for workspace access issues
   * Checks if the current user has access to all data referenced in the bookmark
   */
  private validateBookmarkAccess(bookmark: Bookmark): BookmarkValidationResult {
    const issues: BookmarkValidationIssue[] = [];
    const user = this.activeUserStore.user();

    // Skip validation if user is admin or has full access
    if (!user || user.isAdmin || user.level > 20) {
      return {
        bookmarkId: bookmark._id,
        bookmarkTitle: bookmark.title,
        isValid: true,
        issues: [],
        validatedAt: new Date(),
      };
    }

    // Get user's accessible workspaces
    const userWorkspaceIds = user.workspaces.map(uw => uw.workspace);

    // Check each widget in the bookmark configuration
    if (bookmark.config && Array.isArray(bookmark.config)) {
      bookmark.config.forEach((widget: CustomGridStackWidget) => {
        const widgetIssues = this.validateWidgetAccess(
          widget,
          bookmark,
          userWorkspaceIds
        );
        issues.push(...widgetIssues);
      });
    }

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      isValid: issues.length === 0,
      issues,
      validatedAt: new Date(),
    };
  }

  /**
   * Validates a single widget for workspace access issues
   * Uses the same logic as the public validation but WITHOUT published checks
   */
  private validateWidgetAccess(
    widget: CustomGridStackWidget,
    bookmark: Bookmark,
    userWorkspaceIds: string[]
  ): BookmarkValidationIssue[] {
    const issues: BookmarkValidationIssue[] = [];

    // Skip content widgets
    if (!widget.widget || ['text', 'teaser', 'info'].includes(widget.type)) {
      return issues;
    }

    const widgetConfig = widget.widget;

    // Get all data layers and indicators from widget
    const dataLayerIds = this.getDataLayerIdsFromWidget(widgetConfig);
    const indicatorIds = this.getIndicatorIdsFromWidget(widgetConfig);

    // Check each combination of dataLayer and indicator
    dataLayerIds.forEach(dataLayerId => {
      const dataLayer = this.dataLayerStore.getById(dataLayerId);

      if (!dataLayer) {
        // DataLayer not found in store = user doesn't have workspace access
        issues.push(
          this.createAccessIssue(
            bookmark,
            widget,
            'workspace-restricted-datalayer',
            undefined,
            undefined,
            userWorkspaceIds,
            dataLayerId
          )
        );
        //return; // Skip further checks for this dataLayer
      }

      indicatorIds.forEach(indicatorId => {
        const indicator = this.indicatorStore.getById(indicatorId);

        if (!indicator) {
          // Indicator not found in store = user doesn't have workspace access
          issues.push(
            this.createAccessIssue(
              bookmark,
              widget,
              'workspace-restricted-indicator',
              dataLayer,
              undefined,
              userWorkspaceIds,
              indicatorId
            )
          );
          //return; // Skip further checks for this indicator
        }

        // Note: We don't check indicatorData.published for access validation
        // Access validation only checks if resources exist in user's store
      });
    });

    return issues;
  }

  /**
   * Creates an access-related validation issue object
   */
  private createAccessIssue(
    bookmark: Bookmark,
    widget: CustomGridStackWidget,
    issueType: BookmarkValidationIssue['issueType'],
    dataLayer?: DataLayer,
    indicator?: Indicator,
    userWorkspaceIds?: string[],
    missingId?: string
  ): BookmarkValidationIssue {
    let message = '';

    switch (issueType) {
      case 'workspace-restricted-datalayer':
        message = dataLayer
          ? `Jahr "${dataLayer.year}" ist nicht in Ihrem Arbeitsbereich verfügbar`
          : `Jahr mit ID "${missingId}" ist nicht in Ihrem Arbeitsbereich verfügbar`;
        break;
      case 'workspace-restricted-indicator':
        message = indicator
          ? `Indikator "${indicator.name}" ist nicht in Ihrem Arbeitsbereich verfügbar`
          : `Indikator mit ID "${missingId}" ist nicht in Ihrem Arbeitsbereich verfügbar`;
        break;
      case 'permission-denied-data':
        message = `Sie haben keine Berechtigung für diese Daten`;
        break;
    }

    // Generate widget title
    const widgetTitle = this.getWidgetTitle(widget, indicator);

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      issueType,
      widgetId: String(widget.id),
      widgetType: widget.type,
      widgetHeadline: widgetTitle,
      details: {
        dataLayerId: dataLayer?._id || missingId,
        dataLayerName: dataLayer?.year
          ? String(dataLayer.year)
          : missingId || 'Unbekannt',
        indicatorId: indicator?._id || missingId,
        indicatorName: indicator?.name || missingId || 'Unbekannt',
        workspaceName: undefined, // No longer needed
        requiredWorkspace: undefined, // No longer needed
      },
      message,
    };
  }

  /**
   * Validates a single bookmark for data availability issues
   * Checks if widgets have actual indicator data (regardless of publication status)
   * This validation applies to ALL bookmarks (public and private)
   */
  private validateBookmarkDataAvailability(
    bookmark: Bookmark
  ): BookmarkValidationResult {
    const issues: BookmarkValidationIssue[] = [];

    // Check each widget in the bookmark configuration
    if (bookmark.config && Array.isArray(bookmark.config)) {
      bookmark.config.forEach((widget: CustomGridStackWidget) => {
        const widgetIssues = this.validateWidgetDataAvailability(
          widget,
          bookmark
        );
        issues.push(...widgetIssues);
      });
    }

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      isValid: issues.length === 0,
      issues,
      validatedAt: new Date(),
    };
  }

  /**
   * Validates a single widget for data availability
   * Checks if the widget configuration will display any data
   */
  private validateWidgetDataAvailability(
    widget: CustomGridStackWidget,
    bookmark: Bookmark
  ): BookmarkValidationIssue[] {
    const issues: BookmarkValidationIssue[] = [];

    // Skip content widgets (text, teaser, info) or is external widget-source
    if (
      !widget.widget ||
      ['text', 'teaser', 'info'].includes(widget.type) ||
      widget.widget.context === 'external'
    ) {
      return issues;
    }

    const widgetConfig = widget.widget;
    const focusProperty = widgetConfig.focusProperty;
    const indicatorId = widgetConfig.indicator;
    const dataLayerId = widgetConfig.dataLayer;
    const indicatorIds = this.getIndicatorIdsFromWidget(widgetConfig);
    const dataLayerIds = this.getDataLayerIdsFromWidget(widgetConfig);
    const indicator = indicatorId
      ? this.indicatorStore.getById(indicatorId)
      : undefined;
    const dataLayer = dataLayerId
      ? this.dataLayerStore.getById(dataLayerId)
      : undefined;

    // Handle different focus properties
    if (focusProperty === 'indicator') {
      // Check if indicator has data in any of the dataLayers

      if (!indicatorId) {
        // No indicator configured = no data
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-configured',
            'Kein Indikator konfiguriert',
            undefined,
            widgetConfig.indicators
          )
        );
        return issues;
      }

      // Check if ANY of the dataLayers has data for this indicator
      const hasDataInAnyLayer = dataLayerIds.some(dataLayerId =>
        this.indicatorDataStore.dataLayerAndIndicatorHasData(
          dataLayerId,
          indicatorId
        )
      );

      if (!hasDataInAnyLayer) {
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-available',
            `Indikator "${indicator?.name || indicatorId}" hat keine Daten in den konfigurierten Jahren`,
            indicator,
            undefined,
            dataLayer,
            dataLayerIds
          )
        );
      }
    } else if (focusProperty === 'dataLayer') {
      // Check if dataLayer has data for any of the indicators

      if (!dataLayerId) {
        // No dataLayer configured = no data
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-configured',
            'Kein Jahr konfiguriert'
          )
        );
        return issues;
      }

      // Check if ANY of the indicators has data in this dataLayer
      const indicatorIdsWithNoData = indicatorIds.filter(
        indicatorId =>
          !this.indicatorDataStore.dataLayerAndIndicatorHasData(
            dataLayerId,
            indicatorId
          )
      );

      if (indicatorIdsWithNoData.length === indicatorIds.length) {
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-available',
            `Jahr "${dataLayer?.year || dataLayerId}" hat keine Daten für die konfigurierten Indikatoren`,
            undefined,
            indicatorIdsWithNoData,
            dataLayer
          )
        );
      }
    } else if (focusProperty === 'feature') {
      if (indicatorIds.length === 0 || dataLayerIds.length === 0) {
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-configured',
            'Keine Indikatoren oder Jahre konfiguriert'
          )
        );
        return issues;
      }

      // Check if there's ANY data for the combination
      let hasAnyData = false;
      for (const dataLayerId of dataLayerIds) {
        for (const indicatorId of indicatorIds) {
          if (
            this.indicatorDataStore.dataLayerAndIndicatorHasData(
              dataLayerId,
              indicatorId
            )
          ) {
            hasAnyData = true;
            break;
          }
        }
        if (hasAnyData) break;
      }

      if (!hasAnyData) {
        issues.push(
          this.createDataIssue(
            bookmark,
            widget,
            'no-data-available',
            'Keine Daten für die Kombination aus Indikatoren und Jahren verfügbar',
            undefined,
            indicatorIds,
            undefined,
            dataLayerIds
          )
        );
      }
    }

    return issues;
  }

  /**
   * Creates a data availability issue object
   */
  private createDataIssue(
    bookmark: Bookmark,
    widget: CustomGridStackWidget,
    issueType: 'no-data-configured' | 'no-data-available',
    message: string,
    indicator?: Indicator,
    indicatorIds?: string[],
    dataLayer?: DataLayer,
    dataLayerIds?: string[]
  ): BookmarkValidationIssue {
    const widgetTitle = this.getWidgetTitle(widget, indicator);

    return {
      bookmarkId: bookmark._id,
      bookmarkTitle: bookmark.title,
      issueType,
      widgetId: String(widget.id),
      widgetType: widget.type,
      widgetHeadline: widgetTitle,
      details: {
        dataLayerId: dataLayer?._id,
        dataLayerIds:
          dataLayerIds && dataLayerIds.length ? dataLayerIds : undefined,
        dataLayerName: dataLayer?.year ? String(dataLayer.year) : undefined,
        indicatorId: indicator?._id,
        indicatorIds:
          indicatorIds && indicatorIds.length ? indicatorIds : undefined,
        indicatorName: indicator?.name,
      },
      message,
    };
  }
}
